import { dataHora } from '../../../lib/datas'
import { TERMOS } from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'

/** O título do carimbo. Fica em constante porque o teste que o cobra procura por ele. */
export const TITULO_DA_RESSALVA = 'Registro importado do sistema anterior'

/**
 * O carimbo que impede o RDM de um registro importado de passar por documento do processo.
 *
 * É a parte mais importante deste arquivo, e a razão dela é contratual, não estética. O RDM
 * sai com a cara do formulário oficial: faixa laranja, brasão, campos assinados, "Página 1
 * de 4". Emitir esse documento para uma das 1.027 solicitações trazidas do sistema anterior,
 * sem dizer o que ele é, seria o sistema afirmando por escrito — em papel entregue à
 * fiscalização — uma conformidade que nunca existiu: nenhum desses registros passou pelo
 * comitê no sentido do ANEXO XXI, pela conferência contra o Termo de Referência ou pela
 * comprovação de teste. Impresso, o papel se solta do sistema e não volta mais; o aviso da
 * tela não vai junto. Por isso este bloco existe aqui e é impresso.
 *
 * O `statusOriginalV1` vem com a palavra exata de lá ("Aprovado", "Recusado"), sem tradução:
 * é o que consta nos documentos já arquivados, e é por ele que alguém acha o registro. O
 * `aprovador` é texto livre digitado no sistema antigo — aparece dentro do carimbo, com a
 * explicação do lado, e nunca solto entre os campos do formulário, onde passaria por decisão
 * de comitê registrada.
 *
 * Sai duas vezes no documento, na capa e na folha do formulário. Não é redundância à toa: as
 * folhas do RDM circulam soltas dentro do processo, e a que costuma ser fotocopiada é a do
 * formulário, não a capa.
 *
 * @param props a solicitação importada
 * @returns o carimbo de histórico
 */
export function RessalvaDeHistorico({ gmud }: { gmud: DetalheGmud }) {
  return (
    <aside className="rdm-ressalva">
      <p className="rdm-ressalva-titulo">
        {TITULO_DA_RESSALVA} — não passou pelo {TERMOS.cab.rotulo} ({TERMOS.cab.sigla})
      </p>
      <p>
        Este documento reproduz uma solicitação que já existia no sistema de gestão anterior e foi importada para
        consulta e pesquisa. Ela <strong>não percorreu o processo de gestão de mudanças deste sistema</strong>: não há
        decisão do {TERMOS.cab.rotulo.toLowerCase()} registrada nos termos do ANEXO XXI do Termo de Referência, nem
        conferência contra o Termo de Referência, nem comprovação de teste anexada.
      </p>
      <p>
        Os dados abaixo são os que vieram do sistema anterior, como estavam lá. Este RDM serve como cópia de
        histórico e <strong>não comprova aprovação de mudança</strong>.
      </p>
      <dl className="rdm-ressalva-dados">
        {gmud.statusOriginalV1 && (
          <Procedencia rotulo="No sistema anterior constava como" valor={gmud.statusOriginalV1} />
        )}
        {gmud.aprovador && <Procedencia rotulo="Aprovador registrado lá" valor={gmud.aprovador} />}
        {gmud.migradoEm && <Procedencia rotulo="Importado em" valor={dataHora(gmud.migradoEm)} />}
      </dl>
    </aside>
  )
}

/**
 * Um par da procedência, mantido inteiro na quebra de linha.
 *
 * `<div>` dentro do `<dl>` porque rótulo e valor não podem se separar quando a linha quebra:
 * "Aprovador registrado lá" numa linha e o nome noutra, ao lado de outro rótulo, é pior do
 * que não mostrar — e no papel não há como conferir o que se juntou errado.
 *
 * @param props o rótulo e o valor importados
 * @returns o par
 */
function Procedencia({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt>{rotulo}</dt>
      <dd>{valor}</dd>
    </div>
  )
}
